// src/components/MapView.js
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { rescueEmoji } from '../utils/emojiRescue';
import {
  getUsableVenueAddress,
  getVenueHoursState,
} from '../utils/venueDisplayHelpers';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import '../styles/MapView.css';
import '../styles/VenueDetails.css';

import BathroomWall from './BathroomWall';
import ClaimVenueSheet from './ClaimVenueSheet';

const PARTY_TO_POTTY_EMOJIS = new Set(['🔥', '😍', '🙂', '😐', '💩']);

function partyEmojiForVenue(v) {
  const e = String(v?.emoji || '').trim();

  // The backend score emoji wins.
  if (PARTY_TO_POTTY_EMOJIS.has(e)) return e;

  // Fallback: recreate the Party-to-Potty score from backend scores.
  const score = Number(v?.partyScore ?? v?.nudgedScore ?? v?.rating ?? 0);

  if (!Number.isFinite(score) || score <= 0) return '😐';

  if (score >= 4.725) return '🔥';
  if (score >= 4.45) return '😍';
  if (score >= 4.175) return '🙂';
  if (score >= 3.9) return '😐';
  return '💩';
}

export default function MapView({
  venues = [],
  userLocation,
  deviceLocation,
  selectedVenue,
  anchorVenue,
  onMarkerClick,
  setView,
  mapboxApiKey,
  backendBase = '',
  onBackFromMap,
  onOpenUrl,
  onRouteView,
  partyUid,
  onClaimSubmitted,
  mapDetailMode = 'venue',
}) {
    const hasFitBoundsOnceRef = useRef(false);
    const userHasInteractedRef = useRef(false);
  const mapRef = useRef(null);
  const mapboxRef = useRef(null);
    // Resize Mapbox safely (iOS/WKWebView needs this after layout/nav changes)
    const safeResize = useCallback(() => {
      const m = mapboxRef.current; // Mapbox map instance
      if (!m) return;
      try { m.resize(); } catch {}
      try { m.triggerRepaint?.(); } catch {}
    }, []);
    const [claimToast, setClaimToast] = useState('');
    const markersRef = useRef(new Map());  // place_id -> { marker, el }
  const userDotRef = useRef(null);
  const [mapDebug, setMapDebug] = useState('');
  const routeId = 'pb-route';
  const PINK = '#ff2db3';
  const [mapMountKey, setMapMountKey] = useState(0);
    const lastAutoJumpRef = useRef(0);
  const routeRequestSeqRef = useRef(0);
  const routeViewReportedRef = useRef(new Set());
  const [routeSummary, setRouteSummary] = useState(null);
  // Business-owner photo sheet. Submission and validation live in one reusable component.
  const [claimOpen, setClaimOpen] = useState(false);

  const effectiveBase = useMemo(() => {
      if (/^https?:\/\//i.test(String(backendBase || ''))) return backendBase.replace(/\/+$/, '');
      const w = (typeof window !== 'undefined' && window.__PARTY_BACKEND__) ? String(window.__PARTY_BACKEND__) : '';
      if (/^https?:\/\//i.test(w)) return w.replace(/\/+$/, '');
      const origin = (typeof window !== 'undefined' && window.location?.origin) ? window.location.origin : '';
      if (/^https?:\/\//i.test(origin)) return origin.replace(/\/+$/, '');
      return 'https://partybutton-private-a027fa6fce78.herokuapp.com';
    }, [backendBase]);


  // ----- robust coordinate resolvers -----
  const getLatLng = (v) => {
    if (!v) return null;
    // direct fields
    let lat = v.lat ?? v.latitude;
    let lng = v.lng ?? v.longitude;
    // nested { location: {lat,lng} }
    if (lat == null && v.location?.lat != null) lat = v.location.lat;
    if (lng == null && v.location?.lng != null) lng = v.location.lng;
    // Google geometry.location which may be numbers or LatLng functions
    if (lat == null && v.geometry?.location) {
      const g = v.geometry.location;
      lat = typeof g.lat === 'function' ? g.lat() : g.lat;
      lng = typeof g.lng === 'function' ? g.lng() : g.lng;
    }
    lat = lat != null ? +lat : null;
    lng = lng != null ? +lng : null;
    return Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : null;
  };
  const getLat = (o) => (o?.latitude ?? o?.lat);
  const getLng = (o) => (o?.longitude ?? o?.lng);

  // A neighborhood search may move userLocation to the neighborhood center.
  // Routing and the user dot must prefer the phone's real GPS location.
  const routeOriginLat = getLat(deviceLocation) ?? getLat(userLocation);
  const routeOriginLng = getLng(deviceLocation) ?? getLng(userLocation);
  const routeOriginKey =
    Number.isFinite(routeOriginLat) && Number.isFinite(routeOriginLng)
      ? `${Number(routeOriginLat).toFixed(4)},${Number(routeOriginLng).toFixed(4)}`
      : '';

  const formatWalkingDistance = (meters) => {
    const value = Number(meters);
    if (!Number.isFinite(value) || value < 0) return '';

    const miles = value / 1609.344;
    if (miles < 0.1) {
      return `${Math.max(1, Math.round(value * 3.28084))} ft`;
    }

    return `${miles < 10 ? miles.toFixed(1) : Math.round(miles)} mi`;
  };

  const formatWalkingDuration = (seconds) => {
    const value = Number(seconds);
    if (!Number.isFinite(value) || value < 0) return '';
    return `${Math.max(1, Math.round(value / 60))} min`;
  };

  useEffect(() => {
      mapboxgl.accessToken = mapboxApiKey || process.env.REACT_APP_MAPBOX_API_KEY || '';
  }, [mapboxApiKey]);


  const currentVenue = useMemo(
    () => selectedVenue || anchorVenue || null,
    [selectedVenue, anchorVenue]
  );

  const currentVenueHoursState = useMemo(
    () => getVenueHoursState(currentVenue),
    [currentVenue]
  );

  const currentVenueAddress = useMemo(
    () => getUsableVenueAddress(currentVenue),
    [currentVenue]
  );


    const isSearchAreaDetail =
      mapDetailMode === 'searchArea';

    const isRestroomDetail =
      mapDetailMode === 'restroom';

    const isEventDetail =
      mapDetailMode === 'event' ||
      currentVenue?.isEvent === true ||
      currentVenue?.type === 'event';
   
  // init map
    useEffect(() => {
        const token = mapboxApiKey || process.env.REACT_APP_MAPBOX_API_KEY || '';
        
        const tokenFromProp = !!mapboxApiKey;
        const tokenFromEnv = !!process.env.REACT_APP_MAPBOX_API_KEY;

        console.log('[Mapbox] token source:', tokenFromProp ? 'prop mapboxApiKey' : tokenFromEnv ? 'env REACT_APP_MAPBOX_API_KEY' : 'MISSING');
        console.log('[Mapbox] token present?', !!token, token ? token.slice(0, 8) + '…' : 'MISSING');
        console.log('[Mapbox] origin:', window?.location?.origin);

        if (!token) return; // wait until the key exists
        mapboxgl.accessToken = token;
        
        const lat = getLat(userLocation), lng = getLng(userLocation);
        if (mapboxRef.current || !Number.isFinite(lat) || !Number.isFinite(lng)) return;
        if (!mapRef.current) return;
        const m = new mapboxgl.Map({
            container: mapRef.current,
            style: 'mapbox://styles/mapbox/streets-v12',
            center: [lng, lat],
            zoom: 14
        });
     
        mapboxRef.current = m;
        // iOS WKWebView can create the map before layout settles; keep it sized correctly.
        let ro = null;
        try {
          ro = new ResizeObserver(() => safeResize());
          if (mapRef.current) ro.observe(mapRef.current);
        } catch (_) {}

        // Kick a few resizes (WKWebView sometimes needs a tick)
        safeResize();
        requestAnimationFrame(safeResize);
        setTimeout(safeResize, 250);

        const onWinResize = () => safeResize();
        window.addEventListener('resize', onWinResize);

        m.on('movestart', () => {
            userHasInteractedRef.current = true;
        });
        m.on('zoomstart', () => {
            userHasInteractedRef.current = true;
        });
        
        
    
        m.addControl(new mapboxgl.NavigationControl({ visualizePitch: true }), 'top-right');
        // Helpful diagnostics: if the token/CSP/worker is wrong you'll see it in console.
        m.on('error', (e) => {
          const err = e?.error;
          const msg =
            err?.message ||
            err?.toString?.() ||
            'Unknown Mapbox error';

          const status = err?.status || err?.statusCode;
          const url = err?.url;

          console.warn('[Mapbox] error:', { msg, status, url, raw: e });

          // Keep the on-screen debug short + useful
          const short = [
            status ? `HTTP ${status}` : null,
            msg,
            url ? `(url: ${url})` : null,
          ].filter(Boolean).join(' ');

          setMapDebug(short);
        });


        
        const safeRepaint = () => {
          try { m.resize(); } catch (_) {}
          try { m.triggerRepaint?.(); } catch (_) {}
        };

        const onVis = () => {
          if (document.visibilityState === 'visible') {
            safeRepaint();
            requestAnimationFrame(safeRepaint);
            setTimeout(safeRepaint, 250);
          }
        };

        window.addEventListener('focus', safeRepaint);
        document.addEventListener('visibilitychange', onVis);

        // Capacitor resume (best-effort)
        const capApp = window?.Capacitor?.Plugins?.App;
        let capHandle;
        if (capApp?.addListener) {
          Promise.resolve(
            capApp.addListener('appStateChange', ({ isActive }) => {
              if (isActive) onVis();
            })
          ).then((h) => (capHandle = h)).catch(() => {});
        }

        // WebGL context loss → hard reset
        const canvas = m.getCanvas?.();
        const onLost = (e) => {
          try { e.preventDefault?.(); } catch {}
          setMapDebug('Map paused (WebGL). Reloading…');

          // Remove DOM markers + user dot before nuking refs
          try {
            for (const v of markersRef.current.values()) {
              try { v.marker?.remove?.(); } catch {}
            }
            markersRef.current.clear();
          } catch {}

          try { userDotRef.current?.remove?.(); } catch {}
          userDotRef.current = null;

          try { m.remove(); } catch {}
          mapboxRef.current = null;

          hasFitBoundsOnceRef.current = false;
          userHasInteractedRef.current = false;
          setTimeout(() => setMapMountKey((k) => k + 1), 0);
        };

        canvas?.addEventListener?.('webglcontextlost', onLost, { passive: false });

        
        m.on('load', () => {
            console.log('[Mapbox] load ✅ style:', m.getStyle()?.name || '(no name)', 'zoom:', m.getZoom());
            try { m.resize(); } catch (_) {}
            if (!m.getStyle()) return; // guard against mid-load style swap
            // Pink dashed walking route
            
            // Pink dashed walking route
            if (!m.getSource(routeId)) {
                m.addSource(routeId, { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
            }
            if (!m.getLayer(routeId)) {
                m.addLayer({
                    id: routeId,
                    type: 'line',
                    source: routeId,
                    layout: { 'line-cap': 'round', 'line-join': 'round' },
                    paint: {
                        'line-width': 4,
                        'line-color': PINK,
                        'line-opacity': 0.95,
                        'line-dasharray': [1.2, 1.2]
                    }
                });
            }
            
            addOrRefreshMarkers(); // emojis
            updateUserDot();       // pulsing pink dot
            fitToVisible(true);
            if (currentVenue) drawRouteTo(currentVenue);
        });
        
        return () => {
            try { window.removeEventListener('resize', onWinResize); } catch {}
            try { ro?.disconnect?.(); } catch {}

            
            // Remove DOM markers (Map#remove() does not remove custom Marker elements)
            try {
                for (const v of markersRef.current.values()) {
                    try { v.marker?.remove?.(); } catch {}
                }
                markersRef.current.clear();
            } catch {}
            
            // Finally tear down WebGL map
            try { m.remove(); } catch {}
            mapboxRef.current = null;
            userDotRef.current = null;
            try { window.removeEventListener('focus', safeRepaint); } catch {}
            try { document.removeEventListener('visibilitychange', onVis); } catch {}
            try { capHandle?.remove?.(); } catch {}
            try { canvas?.removeEventListener?.('webglcontextlost', onLost); } catch {}
        };
        
        
        
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userLocation?.latitude, userLocation?.longitude, userLocation?.lat, userLocation?.lng, mapboxApiKey, mapMountKey]);

    useEffect(() => {
      // whenever the UI changes what’s shown, the map container size changes
      setTimeout(safeResize, 80);
    }, [selectedVenue, safeResize]); // or [showMapPanel] / [viewMode] etc


    
    
    // keep user dot in sync
  useEffect(() => { updateUserDot(); }, [userLocation, deviceLocation]); // eslint-disable-line

  // refresh markers when list changes
  useEffect(() => {
    if (!mapboxRef.current) return;
    addOrRefreshMarkers();
    fitToVisible();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [venues]);

  // respond to selection changes
  useEffect(() => {
    raiseActiveMarker();
    if (currentVenue) {
      drawRouteTo(currentVenue);
    } else {
      routeRequestSeqRef.current += 1;
      updateRouteGeojson(null);
      setRouteSummary(null);
    }
    // Re-route when the phone's real GPS position moves materially.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentVenue, routeOriginKey]);
    // Recenter/zoom ONLY when we're following the device and the user hasn't started browsing.
    // Prevents "snap back downtown" when userLocation glitches during search.
    useEffect(() => {
      const m = mapboxRef.current;
      if (!m) return;

      // If the user has panned/zoomed, never hijack the camera.
      if (userHasInteractedRef.current) return;

      // If they’re focused on a venue, don’t auto-jump.
      if (currentVenue) return;

      // Prefer deviceLocation for "follow me" behavior; fall back if missing.
      const lat = getLat(deviceLocation) ?? getLat(userLocation);
      const lng = getLng(deviceLocation) ?? getLng(userLocation);
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;

      // Rate-limit auto jumps (avoid rapid snap / fight loops)
      const now = Date.now();
      if (now - (lastAutoJumpRef.current || 0) < 1500) return;

      const c = m.getCenter();
      const dx = (c.lng - lng) * 111320 * Math.cos((lat * Math.PI) / 180);
      const dy = (c.lat - lat) * 110540;
      const dist = Math.hypot(dx, dy);

      if (dist > 3000) {
        lastAutoJumpRef.current = now;
        m.jumpTo({ center: [lng, lat], zoom: Math.max(m.getZoom(), 13), bearing: 0, pitch: 0 });
        setTimeout(() => { try { m.resize(); } catch (_) {} }, 50);
      }
    }, [
      deviceLocation?.latitude, deviceLocation?.longitude, deviceLocation?.lat, deviceLocation?.lng,
      userLocation?.latitude, userLocation?.longitude, userLocation?.lat, userLocation?.lng,
      currentVenue
    ]);

  // ----- helpers -----
  const updateUserDot = () => {
    const m = mapboxRef.current;
    if (!m) return;

      const lng = getLng(deviceLocation) ?? getLng(userLocation);
      const lat = getLat(deviceLocation) ?? getLat(userLocation);

    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;

    if (!userDotRef.current) {
      const el = document.createElement('div');
      el.className = 'user-location-marker pulse';
      userDotRef.current = new mapboxgl.Marker({ element: el, anchor: 'center' })
        .setLngLat([lng, lat])
        .addTo(m);
    } else {
      userDotRef.current.setLngLat([lng, lat]);
    }
  };

  const idOf = (v) => v?.place_id || v?.id || v?.name;
    
    const styleMarkerEl = (el) => {
      Object.assign(el.style, {
        width: '32px',
        height: '32px',
        borderRadius: '999px',
        background: 'rgba(255,255,255,0.92)',
        border: '2px solid #ff2db3',
        boxShadow: '0 3px 10px rgba(0,0,0,0.28)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily:
          '"Apple Color Emoji", "Segoe UI Emoji", "Noto Color Emoji", -apple-system, BlinkMacSystemFont, sans-serif',
        fontSize: '21px',
        fontWeight: '900',
        lineHeight: '1',
        textAlign: 'center',
      });
    };
    
    function addOrRefreshMarkers() {
        const map = mapboxRef.current;
      if (!map) return;

    // ensure anchor/current are always present
        console.log('[MapView] venues prop length =', venues.length);
        
        const list = [...venues];
    if (currentVenue && !list.some(v => idOf(v) === idOf(currentVenue))) list.push(currentVenue);

    // remove stale
    const incomingIds = new Set(list.map(v => idOf(v)));
    for (const [id, { marker }] of markersRef.current.entries()) {
      if (!incomingIds.has(id)) {
        try { marker.remove(); } catch {}
        markersRef.current.delete(id);
      }
    }

    // add/update
        list.forEach((v, index) => {
      const id = idOf(v);
      const ll = getLatLng(v);
      if (!id || !ll) return;

            let rec = markersRef.current.get(id);
            const markerLabel =
              mapDetailMode === 'restroom' ||
              v?.isRestroom ||
              v?.type === 'restroom'
                ? String(
                    v?.restroomEmoji ||
                    v?.emoji ||
                    '🚻'
                  )
                : v?.isEvent ||
                  v?.type === 'event'
                ? String(
                    v?.emoji ||
                    eventCategoryEmoji(
                      v?.eventCategory
                    )
                  )
                : partyEmojiForVenue(v);




      if (!rec) {
          const el = document.createElement('div');
          el.className = 'emoji-only-marker';
          el.textContent = markerLabel;
          styleMarkerEl(el);
          rescueEmoji(el);

          el.addEventListener('click', () => onMarkerClick && onMarkerClick(v));

        const marker = new mapboxgl.Marker({ element: el, anchor: 'bottom' })
          .setLngLat([ll.lng, ll.lat])
          .addTo(map);

        rec = { marker, el, data: v };
        markersRef.current.set(id, rec);
      } else {
          rec.el.textContent = markerLabel;
          styleMarkerEl(rec.el);
          rescueEmoji(rec.el);
          rec.marker.setLngLat([ll.lng, ll.lat]);
          rec.data = v;
      }
    });

    raiseActiveMarker();
  };

    const fitToVisible = (force = false) => {
      const map = mapboxRef.current;
      if (!map) return;

      // If we've already fit once and the user has interacted, don't snap again
      if (!force && (hasFitBoundsOnceRef.current || userHasInteractedRef.current)) {
        return;
      }

      const ids = Array.from(markersRef.current.keys());
      if (!ids.length) return;

      const bounds = new mapboxgl.LngLatBounds();
      ids.forEach((id) => bounds.extend(markersRef.current.get(id).marker.getLngLat()));
      const lat = routeOriginLat, lng = routeOriginLng;
      if (Number.isFinite(lat) && Number.isFinite(lng)) bounds.extend([lng, lat]);

      try {
        map.fitBounds(bounds, { padding: 40, duration: 400 });
        hasFitBoundsOnceRef.current = true;
      } catch {}
    };


  const raiseActiveMarker = () => {
    for (const { el } of markersRef.current.values()) el.classList.remove('raised');
    if (!currentVenue) return;
    const rec = markersRef.current.get(idOf(currentVenue));
    if (rec) rec.el.classList.add('raised');
  };

  const updateRouteGeojson = (geojson) => {
    const map = mapboxRef.current;
    const source = map?.getSource(routeId);
    if (!source) return false;

    source.setData(
      geojson || { type: 'FeatureCollection', features: [] }
    );
    return true;
  };

  const drawRouteTo = async (venue) => {
    const requestSeq = ++routeRequestSeqRef.current;
    const originLat = routeOriginLat;
    const originLng = routeOriginLng;
    const dest = getLatLng(venue);

    if (
      !dest ||
      !Number.isFinite(originLat) ||
      !Number.isFinite(originLng)
    ) {
      updateRouteGeojson(null);
      setRouteSummary(null);
      return;
    }

    setRouteSummary({ status: 'loading' });

    const start = `${originLng},${originLat}`;
    const end = `${dest.lng},${dest.lat}`;

    try {
      const r = await fetch(
        `${effectiveBase}/api/directions?coords=${encodeURIComponent(
          `${start};${end}`
        )}`
      );

      if (!r.ok) throw new Error(`HTTP ${r.status}`);

      const d = await r.json();
      const route = d?.routes?.[0];
      const line = route?.geometry;

      if (!line) throw new Error('No walking route returned');
      if (requestSeq !== routeRequestSeqRef.current) return;

      const rendered = updateRouteGeojson({
        type: 'FeatureCollection',
        features: [
          {
            type: 'Feature',
            geometry: line,
            properties: {},
          },
        ],
      });

      if (!rendered) return;

      const distanceMeters = Number(route?.distance);
      const durationSeconds = Number(route?.duration);

      setRouteSummary({
        status: 'ready',
        distanceMeters: Number.isFinite(distanceMeters)
          ? distanceMeters
          : null,
        durationSeconds: Number.isFinite(durationSeconds)
          ? durationSeconds
          : null,
      });

      const routeViewKey = [
        idOf(venue) || venue?.name || 'venue',
        Number(originLat).toFixed(4),
        Number(originLng).toFixed(4),
      ].join(':');

      if (!routeViewReportedRef.current.has(routeViewKey)) {
        routeViewReportedRef.current.add(routeViewKey);
        try {
          onRouteView?.(venue, {
            distanceMeters: Number.isFinite(distanceMeters)
              ? distanceMeters
              : null,
            durationSeconds: Number.isFinite(durationSeconds)
              ? durationSeconds
              : null,
          });
        } catch {}
      }
    } catch {
      if (requestSeq !== routeRequestSeqRef.current) return;
      updateRouteGeojson(null);
      setRouteSummary({ status: 'error' });
    }
  };

  const formatEventDateTime = (value) => {
    if (!value) return '';

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
      return '';
    }

    return new Intl.DateTimeFormat(
      'en-US',
      {
        timeZone: 'America/Los_Angeles',
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      }
    ).format(date);
  };

  const eventCategoryEmoji = (category) => {
    switch (
      String(category || '').toLowerCase()
    ) {
      case 'music':
        return '🎵';
      case 'comedy':
        return '😂';
      case 'arts':
        return '🎨';
      case 'sports':
        return '🏟️';
      case 'family':
        return '🎈';
      case 'festival':
        return '🎉';
      default:
        return '🔥';
    }
  };

  const handleStartWalking = () => {
    const dest = getLatLng(currentVenue);
    if (!dest) return;

    const isNativeRuntime = Boolean(
      window?.Capacitor?.isNativePlatform?.() ??
        window?.Capacitor?.isNative
    );

    const destination = `${dest.lat},${dest.lng}`;
    const url = isNativeRuntime
      ? `maps://?daddr=${encodeURIComponent(destination)}&dirflg=w`
      : `https://maps.apple.com/?daddr=${encodeURIComponent(destination)}&dirflg=w`;

    if (typeof onOpenUrl === 'function') {
      onOpenUrl(url, currentVenue);
      return;
    }

    window.location.href = url;
  };

  const posterLoc = {
    latitude:  getLat(deviceLocation) ?? getLat(userLocation),
    longitude: getLng(deviceLocation) ?? getLng(userLocation)
  };
    
    const handleBackToList = () => {
      if (typeof onBackFromMap === 'function') onBackFromMap();
      else if (typeof setView === 'function') setView('list');

      setTimeout(() => safeResize(), 120);
    };

    return (
      <div className="map-view">
            <button onClick={handleBackToList}>
              {isEventDetail
                ? '← 🔥 Events'
                : '← 📍 Nearby Spots'}
            </button>

            <div className="map-hint map-hint-pulse">
            Tap an emoji to preview the spot 👀
            </div>
            <div
              key={mapMountKey}
              ref={mapRef}
              className="map-container"
            style={{ minHeight: 260, position: 'relative' }}
          >
            {!(mapboxApiKey || process.env.REACT_APP_MAPBOX_API_KEY) && (
              <div
                style={{
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: 12,
                  textAlign: 'center',
                  fontSize: 14,
                  opacity: 0.8,
                }}
              >
                Map unavailable (missing Mapbox key).
              </div>
            )}

            {!!mapDebug && (
              <div
                style={{
                  position: 'absolute',
                  left: 8,
                  right: 8,
                  bottom: 8,
                  padding: 8,
                  borderRadius: 10,
                  background: 'rgba(255,255,255,0.9)',
                  fontSize: 12,
                  lineHeight: 1.3,
                }}
              >
                Map error: {mapDebug}
              </div>
            )}
          </div>
            {claimToast ? (
              <div
                style={{
                  position: "fixed",
                  top: "calc(10px + env(safe-area-inset-top))",
                  left: 10,
                  right: 10,
                  zIndex: 10000,
                  padding: "10px 12px",
                  borderRadius: 14,
                  background: "rgba(0,0,0,0.85)",
                  color: "white",
                  fontWeight: 900,
                  fontSize: 13,
                  textAlign: "center",
                  boxShadow: "0 10px 24px rgba(0,0,0,0.22)",
                }}
              >
                {claimToast}
              </div>
            ) : null}
      {/* Details panel — no "Menu" header, just the venue name */}
      <div className="menu-section" style={{ marginTop: 12 }}>
        {currentVenue ? (
          <>
            <div className="menu-venue-name">{currentVenue.name}</div>

                         {!isSearchAreaDetail &&
                           !isRestroomDetail &&
                           !isEventDetail && (
                                                  <div className="venue-facts">                           {/* Open/Closed */}
                                                  <span className={`badge ${currentVenueHoursState.badgeClassName}`}>
                                                  {currentVenueHoursState.label}
                                                  </span>
                                                  
                                                  {/* BOOSTED rating display: displayRating → adjustedRating → raw rating */}
                                                  <span className="badge">
                                                  ★{" "}
                                                  
                                                  {(() => {
                                                      const v = (currentVenue.displayRating ?? currentVenue.adjustedRating ?? currentVenue.rating);
                                                      return (typeof v === 'number' ? v : Number(v || 0)).toFixed(1);
                                                  })()}
                                                  {typeof currentVenue.user_ratings_total === 'number' && (
                                                                                                           <> ({currentVenue.user_ratings_total})</>
                                                                                                           )}
                                                  </span>
                                                  
                                                  
                                                  {/* Price, Walk */}
                                                  {currentVenue.price_level != null && (
                                                                                        <span className="badge">{'$'.repeat(Math.max(1, Math.min(4, currentVenue.price_level)))}</span>
                                                                                        )}
                                                  {currentVenue.walkingTime && <span className="badge">🚶 {currentVenue.walkingTime}</span>}
                                                  
                                                  {/* External Menu pill (prefer backend-scraped link, fall back to venue.menu_url) */}
                                                  {currentVenue.menu_url && (
                                                                                                    <a
                                                                                                    className="badge link"
                                                                                                    href={currentVenue.menu_url}
                                                                                                    target="_blank"
                                                                                                    rel="noopener noreferrer"
                                                                                                    title="View menu"
                                                                                                    >
                                                                                                    📜 Menu
                                                                                                    </a>
                                                                                                    )}
                                                  </div>
                                                  )}


            {currentVenueAddress ? (
              <p className="venue-address">{currentVenueAddress}</p>
            ) : null}

            {isEventDetail ? (
              <div
                style={{
                  marginTop: 10,
                  marginBottom: 10,
                  padding: 14,
                  borderRadius: 16,
                  background:
                    'linear-gradient(135deg, rgba(236,72,153,.13), rgba(139,92,246,.12))',
                  border:
                    '1px solid rgba(236,72,153,.28)',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 11,
                  }}
                >
                  <div
                    aria-hidden="true"
                    style={{
                      flex: '0 0 auto',
                      fontSize: 28,
                      lineHeight: 1,
                    }}
                  >
                    {eventCategoryEmoji(
                      currentVenue?.eventCategory
                    )}
                  </div>

                  <div style={{ minWidth: 0 }}>
                    {currentVenue?.startDateTime ? (
                      <div
                        style={{
                          color: '#c0268a',
                          fontSize: 12,
                          fontWeight: 900,
                          textTransform: 'uppercase',
                          letterSpacing: '.04em',
                        }}
                      >
                        {formatEventDateTime(
                          currentVenue.startDateTime
                        )}
                      </div>
                    ) : null}

                    {currentVenue?.venueName ? (
                      <div
                        style={{
                          marginTop: 4,
                          fontWeight: 900,
                          fontSize: 16,
                        }}
                      >
                        {currentVenue.venueName}
                      </div>
                    ) : null}

                    {currentVenue?.neighborhood ? (
                      <div
                        style={{
                          marginTop: 3,
                          fontSize: 13,
                          opacity: 0.72,
                        }}
                      >
                        {currentVenue.neighborhood}
                      </div>
                    ) : null}
                  </div>
                </div>

                {currentVenue?.description ? (
                  <div
                    style={{
                      marginTop: 11,
                      fontSize: 13,
                      lineHeight: 1.4,
                      opacity: 0.78,
                    }}
                  >
                    {String(
                      currentVenue.description
                    ).slice(0, 500)}
                  </div>
                ) : null}

                {currentVenue?.eventUrl ? (
                  <button
                    type="button"
                    onClick={() =>
                      onOpenUrl?.(
                        currentVenue.eventUrl,
                        currentVenue
                      )
                    }
                    style={{
                      width: '100%',
                      minHeight: 42,
                      marginTop: 12,
                      border: 0,
                      borderRadius: 13,
                      background:
                        'linear-gradient(90deg, #ec4899, #8b5cf6)',
                      color: '#fff',
                      fontWeight: 900,
                      fontSize: 14,
                    }}
                  >
                    Event Details
                  </button>
                ) : null}
              </div>
            ) : null}

            {isRestroomDetail ? (
              <div className="restroom-detail-card">
                <div className="restroom-detail-title">🚻 Potty Report</div>
                <div className="restroom-detail-subtitle">
                  Community updates may change. Confirm access when you arrive.
                </div>
                <div className="restroom-detail-badges">
                  {currentVenue.publicAccess === true ? (
                    <span className="badge">✅ Public</span>
                  ) : currentVenue.publicAccess === false ? (
                    <span className="badge">🔒 Ask first</span>
                  ) : (
                    <span className="badge">❔ Access unknown</span>
                  )}
                  {currentVenue.accessible === true ? <span className="badge">♿ Accessible</span> : null}
                  {currentVenue.openNow === true ? <span className="badge">🟢 Open now</span> : null}
                  {currentVenue.sourceLabel ? <span className="badge">{currentVenue.sourceLabel}</span> : null}
                </div>
              </div>
            ) : null}

            {!isSearchAreaDetail && getLatLng(currentVenue) ? (
              <div
                style={{
                  marginTop: 10,
                  marginBottom: 10,
                  padding: '10px 12px',
                  borderRadius: 14,
                  background: 'rgba(255,45,179,0.08)',
                  border: '1px solid rgba(255,45,179,0.22)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 12,
                  flexWrap: 'wrap',
                }}
              >
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 900, fontSize: 13 }}>
                    {routeSummary?.status === 'ready'
                      ? [
                          formatWalkingDistance(routeSummary.distanceMeters),
                          formatWalkingDuration(routeSummary.durationSeconds),
                        ]
                          .filter(Boolean)
                          .join(' · ') || 'Walking route ready'
                      : routeSummary?.status === 'error'
                      ? 'Route preview unavailable'
                      : 'Calculating walking route…'}
                  </div>
                  <div style={{ marginTop: 2, fontSize: 11, opacity: 0.68 }}>
                    Route starts from your phone’s location
                  </div>
                </div>

                <button
                  type="button"
                  onClick={handleStartWalking}
                  style={{
                    border: 0,
                    borderRadius: 999,
                    padding: '10px 15px',
                    background: PINK,
                    color: '#fff',
                    fontWeight: 900,
                    fontSize: 13,
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                    boxShadow: '0 5px 14px rgba(255,45,179,0.28)',
                  }}
                >
                  🚶 Start Walking
                </button>
              </div>
            ) : null}

                         {!isSearchAreaDetail &&
                           !isRestroomDetail &&
                           !isEventDetail &&
                           !!(
                             currentVenue.tags &&
                             currentVenue.tags.length
                           ) && (
                           <div className="tag-row">
                             {currentVenue.tags.map((t, i) => <span key={i} className="chip">{t}</span>)}
                           </div>
                         )}

                         {!isSearchAreaDetail &&
                           !isRestroomDetail &&
                           !isEventDetail && (
                           <div className="external-menu">
                             <button
                               type="button"
                               className="badge link"
                               onClick={() => setClaimOpen(true)}
                               title="Submit a verified business photo"
                               style={{ border: "none", background: "transparent" }}
                             >
                                                  🏪 Business Owner? Add a Photo
                             </button>
                           </div>
                         )}

            {!isEventDetail && (
              <ClaimVenueSheet
              open={claimOpen}
              venue={currentVenue}
              backendBase={effectiveBase}
              partyUid={partyUid}
              onClose={() => setClaimOpen(false)}
              onSubmitted={(claim) => {
                setClaimToast('✅ Business photo submitted for review.');
                setTimeout(() => setClaimToast(''), 3500);
                onClaimSubmitted?.(currentVenue, claim);
              }}
            />
            )}

          </>
        ) : (
             <div className="map-empty-message">
             <strong>Finding your nearby vibe…</strong>
             <span>Loading venues around you.</span>
             </div>
        )}
      </div>

            {/* Bathroom Wall + News */}
            {currentVenue?.place_id &&
              !isEventDetail && (
              <>
                <div style={{ marginTop: 10 }}>
                  <BathroomWall
                    placeId={currentVenue.place_id}
                    backendBase={effectiveBase}
                    posterLoc={posterLoc}
                    mode={isRestroomDetail ? 'restroom' : 'venue'}
                  />
                </div>

                <div className="map-news-button-wrap">
                  <button
                    type="button"
                    className="map-news-button"
                    onClick={() => setView('headlines')}
                  >
                                        📰 The News
                  </button>
                </div>
              </>
            )}
    </div>
  );
}
